import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleSortState,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { AppDatePipe } from 'src/app/shared/pipes/app-date.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { isSuperAdminRoleSlug } from 'src/app/core/constants/super-admin-role';
import type { RoleRow } from 'src/app/core/services/role-api.service';
import { ActionMenuPanelDirective } from 'src/app/shared/directives/action-menu-panel.directive';

@Component({
  selector: 'app-manage-roles-table',
  standalone: true,
  imports: [
    CommonModule,
    IconModule,
    HasPermissionPipe,
    AppDatePipe,
    ListModuleTableComponent,
    ListCellDefDirective,
    ActionMenuPanelDirective,
  ],
  templateUrl: './manage-roles-table.component.html',
})
export class ManageRolesTableComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @Input() rows: RoleRow[] = [];
  @Input() view: 'active' | 'deleted' = 'active';
  @Input() loading = false;
  @Input() emptyMessage = '';
  @Input() openActionMenuKey: string | null = null;
  @Input() sortColumn = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';

  @Output() sortChange = new EventEmitter<ListModuleSortState>();
  @Output() toggleMenu = new EventEmitter<{ key: string; event: MouseEvent }>();
  @Output() closeMenu = new EventEmitter<void>();
  @Output() edit = new EventEmitter<RoleRow>();
  @Output() delete = new EventEmitter<RoleRow>();
  @Output() permanentDelete = new EventEmitter<RoleRow>();
  @Output() restore = new EventEmitter<RoleRow>();
  @Output() toggleStatus = new EventEmitter<RoleRow>();
  @Output() managePermissions = new EventEmitter<RoleRow>();

  constructor(private readonly router: Router) {}

  get columns(): ListModuleColumnDef[] {
    const activeOnly = this.view === 'active';
    return [
      { id: 'role', header: 'Role', sortable: true, sortKey: 'role_name' },
      { id: 'slug', header: 'Slug', sortable: true, sortKey: 'slug' },
      {
        id: 'date',
        header: this.view === 'deleted' ? 'Deleted' : 'Created',
        sortable: true,
        sortKey: this.view === 'deleted' ? 'deleted_at' : 'created_at',
      },
      {
        id: 'status',
        header: 'Status',
        align: 'center',
        visible: activeOnly,
      },
      { id: 'permissions', header: 'Permissions', visible: activeOnly },
      { id: 'actions', header: 'Action', align: 'right' },
    ];
  }

  actionMenuRowKey(index: number): string {
    return this.view === 'deleted' ? `deleted-${index}` : `active-${index}`;
  }

  isProtectedRole(role: RoleRow): boolean {
    return isSuperAdminRoleSlug(role.slug);
  }

  initials(name: string | null | undefined): string {
    const n = (name ?? '').trim();
    return n ? n.charAt(0).toUpperCase() : '?';
  }

  statusLabel(role: RoleRow): string {
    if (this.view === 'deleted') return 'Deleted';
    return role.is_active ? 'Active' : 'Inactive';
  }

  statusBadgeClass(role: RoleRow): Record<string, boolean> {
    if (this.view === 'deleted') {
      return { 'text-secondary bg-secondary-light': true };
    }
    return role.is_active
      ? { 'text-success bg-success-light': true }
      : { 'text-secondary bg-secondary-light': true };
  }

  goToManagePermissions(role: RoleRow): void {
    if (this.isProtectedRole(role)) return;
    void this.router.navigate(['/system-preferences/manage-permissions', role.id], {
      state: { role },
    });
    this.managePermissions.emit(role);
  }
}
